import styles from './DatabaseERModel.module.css';

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { addEdge, BaseEdge, EdgeLabelRenderer, getBezierPath, Handle, Position } from 'reactflow';
import { useFormikContext } from 'formik';

import UUID from 'bng/utils/UUID';
import Icon from 'components/ui/common/Icon';
import BngIconButton from 'components/bng/ui/BngIconButton';
import ERModel from 'components/ui/common/ERModel';
import BngButton from 'components/bng/ui/BngButton';
import BngDropdown from 'components/bng/ui/BngDropdown';
import useTranslation from 'components/hooks/useTranslation';
import BngCheckbox from 'components/bng/form/BngCheckbox';
import useReduxDispatch from 'components/hooks/useReduxDispatch';
import AssistedModeFilterDialog from 'components/bng/pages/admin/structures/bimQuery/dialogs/AssistedModeFilterDialog';
import { MODALS } from 'components/ui/redux/Actions';
import PreviewDataDialog from 'components/bng/pages/admin/structures/bimQuery/dialogs/PreviewDataDialog';
import AssistedModeJoinDialog from 'components/bng/pages/admin/structures/bimQuery/dialogs/AssistedModeJoinDialog';
import Api from 'components/Api';
import OpConfirmation from 'components/ui/OpConfirmation';
import BngField from 'components/bng/form/BngField';
import UiMsg from 'components/ui/UiMsg';

export const findFieldIcon = (fieldType) => {
  switch (fieldType) {
    case 'NUMERIC':
      return '123';
    case 'DATE':
      return 'schedule';
    case 'BOOL':
      return 'memory';
    default:
      return 'abc';
  }
};

export const JOIN_ICONS = {
  INNER_JOIN: '/resources/images/bimquery/inner_join.svg',
  LEFT_JOIN: '/resources/images/bimquery/left_join.svg',
  RIGHT_JOIN: '/resources/images/bimquery/right_join.svg',
  FULL_OUTER_JOIN: '/resources/images/bimquery/full_outer_join.svg',
};

function DatabaseTableNode({
  id = UUID.randomUUID(),
  data = {},
  onDeleteTable,
  readOnly,
  nodesRequiringAlias,
  setNodesRequiringAlias,
  onSetAlias,
  recalculateTableOrderAfterDeletion,
  reorderTablesOnMainTableChange,
  checkRouteToMainTable,
}) {
  const { t } = useTranslation();
  const dispatch = useReduxDispatch();
  const [aliasValue, setAliasValue] = useState('');

  const tableInfo = data.tableInfo;
  const { values, setFieldValue } = useFormikContext();
  const isEditingThisNode = nodesRequiringAlias.includes(id);
  const isAnyNodeEditing = nodesRequiringAlias.length > 0;
  const isDisabled = readOnly || isAnyNodeEditing;

  const handleFieldAction = useCallback(
    (selectedState, fieldName) => {
      if (isDisabled) {
        return;
      }
      const updatedNodes = values.bimQueryMetadata.nodes.map((node) => {
        if (node.id === id) {
          const updatedFields = node.data.tableInfo.fields.map((field) => {
            if (fieldName && field.name === fieldName) {
              return { ...field, selected: selectedState };
            }
            return fieldName ? field : { ...field, selected: selectedState };
          });

          node = _.cloneDeep(node);
          node.data.tableInfo.fields = updatedFields;
        }

        return node;
      });
      setFieldValue('bimQueryMetadata.nodes', updatedNodes);
    },
    [readOnly, values, id, isDisabled]
  );

  const handleDeleteTable = useCallback(() => {
    if (readOnly) {
      return;
    }
    OpConfirmation({
      title: t('attention'),
      html: t('bim.query.remove.table.warning'),
      onConfirm: () => {
        onDeleteTable(id);
      },
    });
  }, [readOnly, onDeleteTable, id]);

  const openJoinDialog = () => {
    dispatch(
      MODALS.open(AssistedModeJoinDialog, {
        nodeId: id,
        nodes: values.bimQueryMetadata.nodes || [],
        joins: values.bimQueryMetadata.joins || [],
        onChange: ({ nodes, joins }) => {
          setFieldValue('bimQueryMetadata.nodes', nodes);
          setFieldValue('bimQueryMetadata.joins', joins);
        },
        onDeleteTable: handleDeleteTable,
      })
    );
  };

  const hasFilters = useMemo(() => {
    const nodeInValues = values.bimQueryMetadata.nodes.find((node) => node.id === id);
    return nodeInValues?.data?.tableInfo?.filters?.length > 0;
  }, [values.bimQueryMetadata.nodes, id]);

  const handleSetAsMainTable = useCallback(() => {
    OpConfirmation({
      title: t('attention'),
      html: t('bim.query.change.main.table.warning'),
      onConfirm: () => {
        try {
          const { nodes: updatedNodes, joins: updatedJoins } = reorderTablesOnMainTableChange(
            values.bimQueryMetadata.nodes,
            values.bimQueryMetadata.joins || [],
            id
          );
          setFieldValue('bimQueryMetadata.nodes', updatedNodes);
          setFieldValue('bimQueryMetadata.joins', updatedJoins);

          const orphanTables = updatedNodes.filter((node) => {
            if (node.id === id) return false;
            return !checkRouteToMainTable(updatedNodes, updatedJoins, node.id, id);
          });

          if (orphanTables.length > 0) {
            const sortedOrphanTables = orphanTables.sort((a, b) => {
              const orderA = a.data?.tableInfo?.tableOrder || 0;
              const orderB = b.data?.tableInfo?.tableOrder || 0;
              return orderA - orderB;
            });

            const orphanId = sortedOrphanTables[0].id;

            dispatch(
              MODALS.open(AssistedModeJoinDialog, {
                nodeId: orphanId,
                nodes: updatedNodes,
                joins: updatedJoins,
                onChange: ({ nodes, joins }) => {
                  setFieldValue('bimQueryMetadata.nodes', nodes);
                  setFieldValue('bimQueryMetadata.joins', joins);
                },
                onDeleteTable: (nodeIdToDelete) => {
                  const newNodes = updatedNodes.filter((n) => n.id !== nodeIdToDelete);
                  const newTableQueries = values.bimQueryMetadata.tableQueries.filter((tq) => tq.id !== nodeIdToDelete);
                  const newFieldConfigs = values.fieldConfigs?.map((fc) =>
                    fc.nodeId === nodeIdToDelete ? { ...fc, visibleOnUi: false } : fc
                  );
                  const newJoins = updatedJoins.filter(
                    (join) => join.sourceNodeId !== nodeIdToDelete && join.targetNodeId !== nodeIdToDelete
                  );

                  const { nodes: finalNodes, joins: finalJoins } = recalculateTableOrderAfterDeletion(
                    newNodes,
                    newJoins
                  );

                  setFieldValue('bimQueryMetadata.nodes', finalNodes);
                  setFieldValue('bimQueryMetadata.joins', finalJoins);
                  setFieldValue('bimQueryMetadata.tableQueries', newTableQueries);
                  setFieldValue('fieldConfigs', newFieldConfigs, false);

                  dispatch(MODALS.close(AssistedModeJoinDialog));
                },
              })
            );
          }
        } catch (error) {
          console.error('Error on setting main table:', error);
        }
      },
    });
  }, [id, values.bimQueryMetadata.nodes, values.bimQueryMetadata.joins, setFieldValue]);

  const handleCancelAlias = useCallback(() => {
    if (tableInfo.aliasRequired) {
      return;
    }

    setNodesRequiringAlias((prev) => prev.filter((nodeId) => nodeId !== id));
    setAliasValue('');
  }, [setNodesRequiringAlias, id, tableInfo.aliasRequired]);

  const validateAliasUniqueness = useCallback(
    (newAlias) => {
      const currentNode = values.bimQueryMetadata.nodes.find((node) => node.id === id);
      if (!currentNode) return false;

      const hasConflict = values.bimQueryMetadata.nodes.some((node) => {
        if (node.id === id) return false;

        const nodeTableInfo = node.data.tableInfo;

        const trimmedAlias = newAlias.trim().toLowerCase();

        if (nodeTableInfo.alias) {
          return nodeTableInfo.alias.toLowerCase() === trimmedAlias;
        }

        return nodeTableInfo.table.toLowerCase() === trimmedAlias;
      });

      return !hasConflict;
    },
    [values.bimQueryMetadata.nodes, id]
  );

  const handleApplyAlias = useCallback(() => {
    if (!aliasValue.trim()) {
      handleCancelAlias();
      return;
    }

    if (!validateAliasUniqueness(aliasValue)) {
      UiMsg.error(t('name.already.in.use.choose.another.one'));
      return;
    }

    const aliasRegex = /^[A-Za-z0-9_]+$/;
    if (!aliasRegex.test(aliasValue.trim())) {
      UiMsg.error(t('analysis_error_name'));
      return;
    }

    const updatedNodes = values.bimQueryMetadata.nodes.map((node) => {
      if (node.id === id) {
        const { aliasRequired, ...tableInfo } = node.data.tableInfo;
        node = _.cloneDeep(node);
        node.data.tableInfo = {
          ...tableInfo,
          alias: aliasValue.trim(),
        };
      }
      return node;
    });

    setFieldValue('bimQueryMetadata.nodes', updatedNodes);
    setNodesRequiringAlias((prev) => prev.filter((nodeId) => nodeId !== id));
    setAliasValue('');
  }, [
    aliasValue,
    values.bimQueryMetadata.nodes,
    id,
    setFieldValue,
    handleCancelAlias,
    validateAliasUniqueness,
    setNodesRequiringAlias,
  ]);

  const handleKeyPress = useCallback(
    (event) => {
      if (event.key === 'Enter') {
        handleApplyAlias();
      } else if (event.key === 'Escape' && !tableInfo.aliasRequired) {
        handleCancelAlias();
      }
    },
    [handleApplyAlias, handleCancelAlias]
  );

  useEffect(() => {
    if (isEditingThisNode) {
      const currentAlias = tableInfo.alias || tableInfo.table;
      setAliasValue(currentAlias);
    }
  }, [isEditingThisNode, tableInfo.alias, tableInfo.table]);

  useEffect(() => {
    const currentNodes = values.bimQueryMetadata.nodes;
    const currentNode = currentNodes.find((node) => node.id === id);

    if (currentNode && currentNode.data.tableInfo.aliasRequired && currentNode.data.tableInfo.alias) {
      const duplicates = currentNodes.filter((node) => node.data.tableInfo.table === currentNode.data.tableInfo.table);
      const allDuplicatesHaveAlias = (duplicates) => {
        return duplicates.every((node) => node.data.tableInfo.alias && node.data.tableInfo.alias.trim() !== '');
      };

      if (allDuplicatesHaveAlias(duplicates)) {
        const updatedNodes = currentNodes.map((node) => {
          if (node.data.tableInfo.table === currentNode.data.tableInfo.table) {
            const { aliasRequired, ...tableInfo } = node.data.tableInfo;
            return {
              ...node,
              data: {
                ...node.data,
                tableInfo: tableInfo,
              },
            };
          }
          return node;
        });

        setFieldValue('bimQueryMetadata.nodes', updatedNodes);
      }
    }
  }, [values.bimQueryMetadata.nodes, id, setFieldValue]);

  const displayName = tableInfo.alias ? `${tableInfo.alias} (${tableInfo.table})` : tableInfo.table;

  return (
    <div className={`DatabaseTableNode ${styles.DatabaseTableNode}`}>
      <div className={`${styles.databaseTableNode} ${tableInfo.mainTable ? styles.mainTable : ''}`}>
        <div className={styles.tableHeader}>
          <div className={styles.tableName}>
            <Icon icon={'table_chart'} style={{ fontSize: '16px' }} />
            {isEditingThisNode ? (
              <div className={styles.aliasEditContainer}>
                <BngField
                  type="text"
                  value={aliasValue}
                  onChange={(e) => setAliasValue(e.target.value)}
                  withLabel={false}
                  onKeyDown={handleKeyPress}
                  className={`${styles.aliasInput} nodrag`}
                  autoFocus
                  rootClassName="mb-0"
                />
                <div>
                  <BngIconButton
                    icon={'check'}
                    size="small"
                    className={`${styles.actionsButtons} nodrag`}
                    onClick={handleApplyAlias}
                    title={t('apply')}
                  />
                  {!tableInfo.aliasRequired && (
                    <BngIconButton
                      icon={'close'}
                      size="small"
                      className={`${styles.actionsButtons} nodrag`}
                      onClick={handleCancelAlias}
                      title={t('cancel')}
                    />
                  )}
                </div>
              </div>
            ) : (
              <span>{displayName}</span>
            )}
          </div>
          <div className={styles.tableActions}>
            <BngIconButton
              icon={'filter_alt'}
              className={`${hasFilters ? styles.filterButton : ''} nodrag`}
              disabled={isDisabled}
              onClick={() =>
                dispatch(
                  MODALS.open(AssistedModeFilterDialog, {
                    nodeId: id,
                    nodes: values.bimQueryMetadata.nodes,
                    onChange: (nodes) => setFieldValue('bimQueryMetadata.nodes', nodes),
                  })
                )
              }
            />
            <DatabaseERModelDetails
              onSelectAll={() => handleFieldAction(true)}
              onUncheckAll={() => handleFieldAction(false)}
              onDeleteTable={handleDeleteTable}
              onPreviewData={() => {
                dispatch(
                  MODALS.open(PreviewDataDialog, {
                    connectionId: values.connection,
                    nodeId: id,
                    formikValues: values,
                  })
                );
              }}
              onSetMainTable={handleSetAsMainTable}
              isMainTable={tableInfo.mainTable}
              openJoinDialog={openJoinDialog}
              readOnly={readOnly}
              isDisabled={isDisabled}
              onSetAlias={() => onSetAlias(id)}
            />
            <BngIconButton
              icon={
                tableInfo.mainTable ? (
                  'stars'
                ) : (
                  <img
                    src={Api.buildUrl(JOIN_ICONS[tableInfo.joinType] || JOIN_ICONS.INNER_JOIN)}
                    width={24}
                    height={24}
                    alt="Join type image"
                  />
                )
              }
              disabled={isDisabled}
              title={tableInfo.mainTable ? t('main.table') : ''}
              className={`${styles.tableRelationIcon} ${
                tableInfo.mainTable ? styles.mainTableIcon : styles.joinIcons
              } nodrag`}
              onClick={() => {
                if (!tableInfo.mainTable) {
                  openJoinDialog();
                }
              }}
            />
          </div>
        </div>
        <div className={styles.fieldWrapper}>
          {tableInfo.fields.map((field, idx) => {
            const handleId = `${id}-field-${field.name}`;
            return (
              <div className={styles.field} key={idx}>
                {!readOnly && (
                  <>
                    <Handle
                      type="target"
                      id={`target-left-${handleId}`}
                      position={Position.Left}
                      style={{
                        top: '50%',
                        left: '-8px',
                      }}
                      className={`${styles.tableHandle}`}
                      isConnectable={!readOnly || !isAnyNodeEditing}
                    />
                    <Handle
                      type="source"
                      id={`source-left-${handleId}`}
                      position={Position.Left}
                      style={{
                        top: '50%',
                        left: '-8px',
                      }}
                      className={`${styles.tableHandle}`}
                      isConnectable={!readOnly || !isAnyNodeEditing}
                    />

                    <Handle
                      type="target"
                      id={`target-right-${handleId}`}
                      position={Position.Right}
                      style={{
                        top: '50%',
                        right: '-8px',
                      }}
                      className={`${styles.tableHandle}`}
                      isConnectable={!readOnly || !isAnyNodeEditing}
                    />
                    <Handle
                      type="source"
                      id={`source-right-${handleId}`}
                      position={Position.Right}
                      style={{
                        top: '50%',
                        right: '-8px',
                      }}
                      className={`${styles.tableHandle}`}
                      isConnectable={!readOnly || !isAnyNodeEditing}
                    />
                  </>
                )}

                <BngCheckbox
                  className="nodrag"
                  disabled={isDisabled}
                  field={{
                    onChange: () => handleFieldAction(!field.selected, field.name),
                    value: field.selected,
                  }}
                  style={{ minHeight: '15px' }}
                />
                <Icon icon={findFieldIcon(field.type)} />
                <span>{field.name}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function DatabaseEdge({ id = UUID.randomUUID(), selected = false, source, target, onDeleteTable, readOnly, ...props }) {
  const dispatch = useReduxDispatch();
  const { values, setFieldValue } = useFormikContext();
  const [edgePath, labelX, labelY] = getBezierPath(props);

  const handleEdgeClick = useCallback(() => {
    if (readOnly) {
      return;
    }
    const sourceNode = values.bimQueryMetadata.nodes.find((node) => node.id === source);
    const targetNode = values.bimQueryMetadata.nodes.find((node) => node.id === target);

    if (!sourceNode || !targetNode) {
      return;
    }

    const sourceOrder = sourceNode.data?.tableInfo?.tableOrder || 0;
    const targetOrder = targetNode.data?.tableInfo?.tableOrder || 0;

    const nodeToOpen = sourceOrder > targetOrder ? sourceNode : targetNode;

    const sourceField = props.sourceHandleId?.split('-field-').pop();
    const targetField = props.targetHandleId?.split('-field-').pop();
    const highlightedField = sourceOrder > targetOrder ? sourceField : targetField;

    dispatch(
      MODALS.open(AssistedModeJoinDialog, {
        nodeId: nodeToOpen.id,
        nodes: values.bimQueryMetadata.nodes || [],
        joins: values.bimQueryMetadata.joins || [],
        highlightedField: highlightedField,
        onChange: ({ nodes, joins }) => {
          setFieldValue('bimQueryMetadata.nodes', nodes);
          setFieldValue('bimQueryMetadata.joins', joins);
        },
        onDeleteTable: onDeleteTable,
      })
    );
  }, [readOnly, source, target, values, dispatch, setFieldValue, onDeleteTable]);

  return (
    <>
      <BaseEdge id={id} path={edgePath} {...props} />
      <EdgeLabelRenderer>
        <div
          className={`DatabaseEdge ${styles.databaseEdge}`}
          style={{
            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
          }}
        >
          <BngButton icon={'link'} disabled={readOnly} className={styles.edgeIconButton} onClick={handleEdgeClick} />
        </div>
      </EdgeLabelRenderer>
    </>
  );
}

function DatabaseERModelDetails({
  onSelectAll,
  onUncheckAll,
  onDeleteTable,
  onPreviewData,
  onSetMainTable,
  isMainTable,
  openJoinDialog,
  readOnly,
  onSetAlias,
  isDisabled,
}) {
  const { t } = useTranslation();

  return (
    <BngDropdown
      className="nodrag"
      style={{ height: '250px' }}
      popperClassName={styles.bngDropdown}
      customButton={({ openDropdown }) => {
        return (
          <BngIconButton
            icon={'more_vert'}
            disabled={readOnly}
            onClick={(e) => {
              e.stopPropagation();
              openDropdown(e);
            }}
            className={styles.headersButtons}
          />
        );
      }}
      options={[
        [
          {
            label: t('structure.preview'),
            icon: 'visibility',
            onClick: onPreviewData,
            disabled: isDisabled,
          },
          {
            label: t('remove.structure'),
            icon: 'close',
            onClick: onDeleteTable,
            disabled: isMainTable,
            title: isMainTable ? t('impossible.to.remove.main.table') : '',
          },
          {
            label: t('set.alias'),
            icon: (
              <img
                src={Api.buildUrl('/resources/images/bimquery/insert_text.svg')}
                width={16}
                height={16}
                alt="Set alias image"
              />
            ),
            onClick: onSetAlias,
            disabled: isDisabled,
          },
          {
            label: t('set.as.main.table'),
            icon: 'stars',
            onClick: onSetMainTable,
            disabled: isMainTable || isDisabled,
          },
          {
            label: t('bim.query.join'),
            icon: <img src={Api.buildUrl(JOIN_ICONS.INNER_JOIN)} width={16} height={16} alt="Join type image" />,
            onClick: openJoinDialog,
            disabled: isMainTable || isDisabled,
          },
        ],
        [
          {
            label: t('select.all.columns'),
            icon: 'check',
            onClick: onSelectAll,
            disabled: isDisabled,
          },
          {
            label: t('uncheck.all.columns'),
            icon: 'remove_done',
            onClick: onUncheckAll,
            disabled: isDisabled,
          },
        ],
      ]}
    />
  );
}

const DatabaseERModelContext = createContext({
  readOnly: false,
  onDeleteTable: _.noop,
});

function DatabaseTableNodeWrapper(props) {
  const {
    onDeleteTable,
    readOnly,
    nodesRequiringAlias,
    setNodesRequiringAlias,
    onSetAlias,
    recalculateTableOrderAfterDeletion,
    reorderTablesOnMainTableChange,
    checkRouteToMainTable,
  } = useContext(
    DatabaseERModelContext
  );

  return (
    <DatabaseTableNode
      {...props}
      onDeleteTable={onDeleteTable}
      readOnly={readOnly}
      nodesRequiringAlias={nodesRequiringAlias}
      setNodesRequiringAlias={setNodesRequiringAlias}
      onSetAlias={onSetAlias}
      recalculateTableOrderAfterDeletion={recalculateTableOrderAfterDeletion}
      reorderTablesOnMainTableChange={reorderTablesOnMainTableChange}
      checkRouteToMainTable={checkRouteToMainTable}
    />
  );
}

function DatabaseEdgeWrapper(props) {
  const { onDeleteTable, readOnly, nodesRequiringAlias } = useContext(DatabaseERModelContext);
  const isUIBlocked = readOnly || nodesRequiringAlias.length > 0;
  return <DatabaseEdge {...props} onDeleteTable={onDeleteTable} readOnly={isUIBlocked} />;
}

const NODE_TYPES = {
  databaseTable: DatabaseTableNodeWrapper,
};

const EDGE_TYPES = {
  databaseEdge: DatabaseEdgeWrapper,
};

export default function DatabaseERModel({
  initialNodes,
  onNewNode = _.noop,
  formikProps,
  readOnly = false,
  onAddTable = _.noop,
  recalculateTableOrderAfterDeletion = _.noop,
  reorderTablesOnMainTableChange = _.noop,
  checkRouteToMainTable = _.noop,
  onDeleteTable = _.noop,
}) {
  const dispatch = useReduxDispatch();
  const [nodesRequiringAlias, setNodesRequiringAlias] = useState([]);

  useEffect(() => {
    const nodes = formikProps.values.bimQueryMetadata.nodes || [];
    if (nodes.length === 0) {
      setNodesRequiringAlias([]);
      return;
    }

    const tableCounts = _.countBy(nodes, 'data.tableInfo.table');
    const duplicateTableNames = Object.keys(tableCounts).filter((name) => tableCounts[name] > 1);

    let nodesThatNeedAlias = [];
    let needsUpdateInFormik = false;

    const updatedNodes = nodes.map((node) => {
      const tableInfo = node.data.tableInfo;
      const isDuplicate = duplicateTableNames.includes(tableInfo.table);
      const needsAlias = isDuplicate && !tableInfo.alias;

      if (needsAlias) {
        nodesThatNeedAlias.push(node.id);
      }

      if ((tableInfo.aliasRequired || false) !== needsAlias) {
        needsUpdateInFormik = true;
        const newTableInfo = { ...tableInfo, aliasRequired: needsAlias };
        return { ...node, data: { ...node.data, tableInfo: newTableInfo } };
      }

      return node;
    });

    setNodesRequiringAlias((prev) => {
      const manualOnly = prev.filter(nodeId => {
        const node = nodes.find(n => n.id === nodeId);
        if (!node) return false;
        return !duplicateTableNames.includes(node.data.tableInfo.table);
      });

      const newAliasList = [...manualOnly, ...nodesThatNeedAlias];
      return [...new Set(newAliasList)];
    });

    if (needsUpdateInFormik) {
      formikProps.setFieldValue('bimQueryMetadata.nodes', updatedNodes, false);
    }
  }, [formikProps.values.bimQueryMetadata.nodes]);

  const handleManualSetAlias = useCallback((nodeId) => {
    setNodesRequiringAlias((prev) => [...new Set([...prev, nodeId])]);
  }, []);

  const contextValue = useMemo(
    () => ({
      onDeleteTable,
      readOnly,
      nodesRequiringAlias,
      setNodesRequiringAlias,
      onSetAlias: handleManualSetAlias,
      recalculateTableOrderAfterDeletion,
      reorderTablesOnMainTableChange,
      checkRouteToMainTable,
    }),
    [
      onDeleteTable,
      readOnly,
      nodesRequiringAlias,
      handleManualSetAlias,
      recalculateTableOrderAfterDeletion,
      reorderTablesOnMainTableChange,
      checkRouteToMainTable
    ]
  );

  return (
    <DatabaseERModelContext.Provider value={contextValue}>
      <ERModel
        className="DatabaseERModel"
        showControls={true}
        showMinimap={false}
        showNewNodeButton={false}
        initialNodes={initialNodes}
        nodesDraggable={!readOnly && nodesRequiringAlias.length === 0}
        elementsSelectable={!readOnly && nodesRequiringAlias.length === 0}
        panOnDrag
        onNewNode={onNewNode}
        onChange={({ nodes, joins }) => {
          formikProps.setFieldValue('bimQueryMetadata.nodes', nodes);
          formikProps.setFieldValue('bimQueryMetadata.joins', joins);
        }}
        nodeTypes={NODE_TYPES}
        edgeTypes={EDGE_TYPES}
        onNewEdge={({ params, setEdges, nodes }) => {
          if (readOnly) {
            return;
          }

          const { source, target, sourceHandle, targetHandle } = params;

          if (source === target) {
            return;
          }

          const sourceField = sourceHandle.split('-field-').pop();
          const targetField = targetHandle.split('-field-').pop();

          const sourceNode = nodes.find((node) => node.id === source);
          const targetNode = nodes.find((node) => node.id === target);

          const tableOrder =
            sourceNode.data.tableInfo.tableOrder > targetNode.data.tableInfo.tableOrder
              ? sourceNode.data.tableInfo.tableOrder
              : targetNode.data.tableInfo.tableOrder;

          const newJoin = {
            id: UUID.generate(),
            sourceNodeId: sourceNode.id,
            sourceNodeName: sourceNode.data.tableInfo.table,
            sourceField: sourceField,
            targetNodeId: targetNode.id,
            targetNodeName: targetNode.data.tableInfo.table,
            targetField: targetField,
            joinType: 'INNER_JOIN',
            logicalOperator: 'AND',
            operator: '=',
            tableOrder: tableOrder,
          };

          const edgeId = `join-${newJoin.id}`;

          const newEdge = {
            id: edgeId,
            source: source,
            target: target,
            type: 'databaseEdge',
            sourceHandle: sourceHandle,
            targetHandle: targetHandle,
            style: {
              strokeWidth: 2,
              stroke: '#005dff',
            },
          };

          setEdges((eds) => addEdge(newEdge, eds));

          const currentJoins = formikProps.values.bimQueryMetadata.joins || [];
          formikProps.setFieldValue('bimQueryMetadata.joins', [...currentJoins, newJoin]);
        }}
        joins={formikProps.values.bimQueryMetadata.joins}
        onDeleteTable={onDeleteTable}
        onAddTable={onAddTable}
      />
    </DatabaseERModelContext.Provider>
  );
}
